import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { FC, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import InfoIcon from '@material-ui/icons/Info';
import Popover from '@material-ui/core/Popover';
import { format as dateFormat } from 'date-fns';
import { DateRange } from 'react-date-range';
import CircularProgress from '@material-ui/core/CircularProgress';

import useSidebar from 'src/hooks/useSidebar';
import { Dashboard } from 'src/types';
import { DashboardFilter } from '../sidebar/DashboardFilter';
import { ReactComponent as SearchIcon } from 'src/assets/icons/searchFilled.svg';
import InfoTooltip from 'src/components/InfoTooltip';
import { NewDashboardButton } from 'src/components/dashboard/MenuRow/MenuRow';
import { useTextStyles } from 'src/hooks/useTextStyles';

import { FilterDateType } from './index';

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: 24,
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      background: '#fff',
      height: '100%',
      overflow: 'auto',
      width: 390,
      borderRight: '1px solid rgba(0, 0, 0, 0.12)',
      zIndex: 100,
    },
    publishersContainer: {
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderRadius: 4,
      padding: 5,
      marginTop: 4,
      gap: 12,
      minHeight: 50,
      cursor: 'pointer',
      overflow: 'auto',
      maxHeight: 350,
      '& .MuiFormControlLabel-root': {
        marginLeft: 0,
      },
      '&::-webkit-scrollbar': {
        width: 6,
      },
      '&::-webkit-scrollbar-track ': {
        background: '#f1f1f1',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: 3,
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#555',
      },

      '& > div': {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        background: 'rgba(62, 145, 222, 0.5)',
        borderRadius: 20,
        padding: '0 8px',
        height: 24,
        color: '#3E91DE',
        fontSize: 13,
      },
    },
    selectedDashboard: {
      width: '100%',
      borderRadius: 3,
      border: '1px solid #C3C3C7',
      background: '#FFF',
      padding: 8,
      marginTop: 12,
      marginBottom: 4,
      cursor: 'pointer',
      position: 'relative',
      paddingRight: 32,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',

      '& > .arrowIcon': {
        position: 'absolute',
        top: '55%',
        right: 8,
        transform: 'translate(0, -50%)',
        '& > svg': {
          fontSize: 20,
        },
      },
    },
    header: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      borderBottom: '1px solid #EAECF0',
      background: '#FFF',
      padding: '8px 0',
      '& > svg': {
        width: 16,
        height: 16,
      },

      '& > input': {
        height: 20,
        border: 'none',
        padding: 0,
        margin: 0,
        flex: 1,
        outline: 'none',
        fontSize: 14,
        lineHeight: '20px',
      },
    },
    tooltip: {
      minWidth: 510,
      background: 'gray',
    },
    moreFilterOptions: {
      gap: 20,
      width: '100%',
      marginTop: 30,
    },
    fitlerOptionTitle: {
      fontWeight: 'bold',
      marginRight: 12,
    },
    filterType: {
      borderStyle: 'solid',
      borderWidth: 2,
      borderColor: 'gray',
      borderRadius: 20,
      display: 'flex',
      overflow: 'hidden',
      '& > div': {
        padding: '1px 4px',
        textAlign: 'center',
        fontSize: 12,
        fontWeight: 'normal',
        cursor: 'pointer',
        textTransform: 'capitalize',
        borderRight: '2px solid #3E91DE',
        '&:hover': {
          background: '#3E91DE30',
        },
        '&:last-child': {
          border: 'none',
        },
      },
    },
    iconButton: {
      padding: 0,
      width: 24,
      height: 24,
      marginLeft: 8,
    },
    filterBtn: {
      fontWeight: 500,
      fontSize: 20,
      color: 'white',
      minWidth: 'auto',
      width: '100%',
      borderRadius: 24,
      boxShadow: '0px 0px 0px 1px #E0E0E0',
      marginTop: 'auto',
    },
    loading: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 16,
      marginBottom: 4,
    },
  })
);

interface SearchFilterSectionProps {
  dashboards: Dashboard[];
  selectedDashboardId: Nullable<string>;
  setSelectedDashboardId: (dashboard: Nullable<string>) => void;
  handleSearch: (
    text: string,
    mode: string,
    operator: string,
    match: string,
    dateRange: {
      startDate?: Date;
      endDate?: Date;
    },
    page: number,
    pageSize: number,
    dataLevel: string
  ) => void;
  disableSearch: boolean;
  operatorSelect: string;
  setOperatorSelect: (item: string) => void;
  matchSelect: string;
  setMatchSelect: (item: string) => void;
  sortMode: string;
  setSortMode: (item: string) => void;
  filterDateRange: FilterDateType;
  setFilterDateRange: (item: FilterDateType) => void;
  keyword: string;
  setKeyword: (item: string) => void;
  page: number;
  pageSize: number;
  loadingShows: boolean;
  dataLevel: string;
}

const SearchFilterSection: FC<SearchFilterSectionProps> = ({
  dashboards,
  selectedDashboardId,
  setSelectedDashboardId,
  handleSearch,
  disableSearch = false,
  operatorSelect,
  setOperatorSelect,
  matchSelect,
  setMatchSelect,
  sortMode,
  setSortMode,
  filterDateRange,
  setFilterDateRange,
  keyword,
  setKeyword,
  page,
  pageSize,
  loadingShows,
  dataLevel,
}) => {
  const textClasses = useTextStyles();
  const [openDashboardList, setOpenDashboardList] = useState<boolean>(false);
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClickDropDownIcon = (event: any) => {
    setShowRangePicker(true);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseRangePicker = () => {
    setShowRangePicker(false);
  };

  const { isSmallSidebar } = useSidebar();

  const classes = useStyles();

  return (
    <Box className={classes.container}>
      <Typography style={{ fontSize: 20, marginBottom: 16 }}>Search</Typography>
      <Grid container>
        <Grid
          container
          style={{
            marginTop: 10,
            marginBottom: 30,
          }}
        >
          <Box className={classes.header}>
            <SearchIcon fill='none' stroke='#667085' />
            <input
              style={{ flex: 1 }}
              placeholder='Search by Episode name, GARM term, IAB category, or Host name'
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <InfoTooltip
              style={{ marginLeft: 4 }}
              placement='left'
              classes={{ tooltip: classes.tooltip }}
              content={
                <Typography>
                  Use these tips to search for anything across the Barometer
                  library:
                  <br />
                  <span
                    style={{ fontSize: 14, paddingTop: 8, display: 'block' }}
                  >
                    Add commas <b>[search a, search b]</b> to make multiple
                    queries at the same time.
                    <br />
                    Toggle to <b>AND</b> to require all terms being present at
                    the same time.
                    <br />
                    Toggle to <b>OR</b> to see results for any of the listed
                    terms.
                    <br />
                    Toggle to <b>EXACT</b> to see results that are exact matches
                    of your search.
                    <br />
                    Toggle to <b>PARTIAL</b> to see results that are exact
                    matches of your search.
                    <br />
                    Utilize{' '}
                    <b>
                      &apos;Relevant&apos; | &apos;Newest&apos; |
                      &apos;Oldest&apos;
                    </b>{' '}
                    to guide the results to your preferences.
                    <br />
                    Use the <b>Date Range</b> filter to specify a certain time
                    range for your results.
                  </span>
                </Typography>
              }
            >
              <InfoIcon />
            </InfoTooltip>
          </Box>
          <Box
            display='flex'
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            className={classes.moreFilterOptions}
          >
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              justifyContent='space-between'
              className='TimeRangeSelector'
              width='100%'
            >
              <span className={classes.fitlerOptionTitle} style={{ flex: 1 }}>
                Published Date:{' '}
              </span>
              <span style={{ width: 180, textAlign: 'right' }}>
                {filterDateRange.startDate
                  ? [
                      dateFormat(
                        filterDateRange.startDate || new Date(),
                        'MMM dd, yyyy'
                      ),
                      dateFormat(
                        filterDateRange.endDate || new Date(),
                        'MMM dd, yyyy'
                      ),
                    ].join(' - ')
                  : 'No Date Filter'}
              </span>
              <IconButton
                className={classes.iconButton}
                onClick={handleClickDropDownIcon}
                aria-describedby='search-date-range-popover'
              >
                <ArrowDropDownIcon style={{ fontSize: 24 }} />
              </IconButton>

              <Popover
                id='search-date-range-popover'
                open={showRangePicker}
                anchorEl={anchorEl}
                onClose={handleCloseRangePicker}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <Box
                  display='flex'
                  flexDirection='column'
                  justifyContent='center'
                >
                  <DateRange
                    editableDateInputs={true}
                    onChange={(item) =>
                      setFilterDateRange({
                        startDate: item.selection.startDate,
                        endDate: item.selection.endDate,
                      })
                    }
                    moveRangeOnFirstSelection={false}
                    ranges={[{ ...filterDateRange, key: 'selection' }]}
                  />
                </Box>
              </Popover>
            </Box>
          </Box>
          <Box
            display='flex'
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            className={classes.moreFilterOptions}
          >
            <Box className={classes.filterType}>
              {['Exact', 'Partial'].map((item) => (
                <div
                  key={item}
                  style={
                    item.toLowerCase() === matchSelect
                      ? { background: '#3E91DE80' }
                      : undefined
                  }
                  onClick={() => setMatchSelect(item.toLowerCase())}
                >
                  {item}
                </div>
              ))}
            </Box>
            <Box className={classes.filterType}>
              {['AND', 'OR'].map((item) => (
                <div
                  key={item}
                  style={
                    item === operatorSelect
                      ? { background: '#3E91DE80' }
                      : undefined
                  }
                  onClick={() => setOperatorSelect(item)}
                >
                  {item}
                </div>
              ))}
            </Box>
            <Box className={classes.filterType}>
              {['Relevant', 'Newest', 'Oldest'].map((item) => (
                <div
                  key={item}
                  style={
                    item.toLowerCase() === sortMode
                      ? { background: '#3E91DE80' }
                      : undefined
                  }
                  onClick={() => setSortMode(item.toLowerCase())}
                >
                  {item}
                </div>
              ))}
            </Box>
          </Box>
        </Grid>
        <Grid
          container
          style={{
            marginTop: 30,
            marginBottom: 30,
          }}
        >
          <Grid container item direction='column'>
            <Typography
              style={{
                fontSize: 16,
                fontWeight: 'normal',
                paddingRight: 8,
                color: '#2A2A2E',
              }}
            >
              Dashboard
            </Typography>
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                !loadingShows && setOpenDashboardList(!openDashboardList);
              }}
              className={classes.selectedDashboard}
            >
              {selectedDashboardId
                ? dashboards.find((item) => item.id === selectedDashboardId)
                    ?.name
                : 'All Barometer Content'}
              <div className='arrowIcon'>
                {openDashboardList ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </div>
            </div>
            {!!loadingShows && (
              <Box className={classes.loading}>
                <CircularProgress size={18} />
                <Typography
                  className={textClasses.smRegular}
                  style={{ marginTop: 4 }}
                >
                  Getting List of Shows from Selected Dashboard
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>
      <DashboardFilter
        filters={dashboards}
        isOpen={openDashboardList}
        isSmallSidebar={isSmallSidebar}
        handleClose={() => {
          setOpenDashboardList(false);
        }}
        handleSelect={(item) => {
          setSelectedDashboardId(item ? item.id : null);
          setOpenDashboardList(false);
        }}
      />
      <NewDashboardButton
        variant='contained'
        onClick={() => {
          !disableSearch &&
            keyword &&
            handleSearch(
              keyword,
              sortMode,
              operatorSelect,
              matchSelect,
              filterDateRange,
              page,
              pageSize,
              dataLevel
            );
        }}
        color='primary'
        className={classes.filterBtn}
        disabled={disableSearch}
      >
        Apply Search
      </NewDashboardButton>
    </Box>
  );
};

export default SearchFilterSection;
